import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface SMSTemplateData {
  id: string;
  sms_title: string;
  msg: string;
  status: boolean;
  templateId?: string;
  created_at?: string;
  updated_at?: string;
}

interface SMSTemplatesResponse {
  statusCode: number;
  message: string;
  data: {
    data: SMSTemplateData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface SMSTemplatesState {
  templatesData: SMSTemplatesResponse | null;
  templateDetails: any;
  createData: any;
  deleteData: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "create" | "get_by_id" | "delete" | null;
}

const smsTemplatesInitialState: SMSTemplatesState = {
  templatesData: null,
  templateDetails: null,
  createData: null,
  deleteData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getSMSTemplates: any = createAsyncThunk(
  "smsTemplates/getSMSTemplates",
  async (payload: { page?: number; limit?: number; query?: string }, thunkAPI) => {
    try {
      const requestBody = {
        page: payload.page || 1,
        limit: payload.limit || 10,
        query: payload.query || "",
      };

      const response = await Api.post(API_URLS.SMS_TEMPLATES.LIST, requestBody);

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createSMSTemplate: any = createAsyncThunk(
  "smsTemplates/createSMSTemplate",
  async (
    payload: {
      id?: string;
      sms_title: string;
      msg: string;
      status: boolean;
      templateId: string;
    },
    thunkAPI
  ) => {
    try {
      const requestBody = {
        ...(payload.id && { id: payload.id }),
        sms_title: payload.sms_title,
        msg: payload.msg,
        status: payload.status,
        templateId: payload.templateId,
      };

      const response = await Api.post(
        API_URLS.SMS_TEMPLATES.CREATE,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getSMSTemplateById: any = createAsyncThunk(
  "smsTemplates/getSMSTemplateById",
  async (payload: { id: string }, thunkAPI) => {
    try {
      const requestBody = {
        id: payload.id,
      };

      const response = await Api.post(
        API_URLS.SMS_TEMPLATES.GET_BY_ID,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteSMSTemplate: any = createAsyncThunk(
  "smsTemplates/deleteSMSTemplate",
  async (payload: { id: string }, thunkAPI) => {
    try {
      const requestBody = {
        id: payload.id,
      };

      const response = await Api.post(
        API_URLS.SMS_TEMPLATES.DELETE,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const resetAll = () => ({
  type: "smsTemplates/resetAll",
});

const smsTemplatesSlice = createSlice({
  name: "smsTemplatesState",
  initialState: smsTemplatesInitialState,
  reducers: {
    resetAll: (state) => {
      state.templatesData = null;
      state.templateDetails = null;
      state.createData = null;
      state.deleteData = null;
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get SMS Templates
      .addCase(getSMSTemplates.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
      })
      .addCase(getSMSTemplates.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.templatesData = action.payload;
        state.error = null;
      })
      .addCase(getSMSTemplates.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Create SMS Template
      .addCase(createSMSTemplate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "create";
      })
      .addCase(createSMSTemplate.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.createData = action.payload;
        state.error = null;
      })
      .addCase(createSMSTemplate.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Get SMS Template by ID
      .addCase(getSMSTemplateById.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "get_by_id";
      })
      .addCase(getSMSTemplateById.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.templateDetails = action.payload;
        state.error = null;
      })
      .addCase(getSMSTemplateById.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Delete SMS Template
      .addCase(deleteSMSTemplate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "delete";
      })
      .addCase(deleteSMSTemplate.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.deleteData = action.payload;
        state.error = null;
      })
      .addCase(deleteSMSTemplate.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      });
  },
});

export const { resetAll: resetSMSTemplatesAll } = smsTemplatesSlice.actions;
export default smsTemplatesSlice.reducer;
